import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { useApi } from "@/api";
import {
  type ConnectionOut,
  ConnectionChip,
  SyncDetail,
  type DatasetSourceInfo,
  type Json,
  ProviderIcon,
  type SourceOut,
  FolderBrowser,
  FrequencySelect,
  MailboxPicker,
  containerRefLabel,
} from "@/components/manage/connectors";
import { EmptyRow, Section } from "@/components/manage/Section";
import { PageHeader } from "@/components/manage/PageHeader";
import { StatStrip } from "@/components/manage/StatStrip";
import { useActiveOrgId, useIsDemoManage } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/datasets/")({
  component: DatasetsPage,
});

interface DatasetSummary {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  file_count: number;
}

// Workspaces the org owns, as returned by /admin/orgs/{orgId}/workspaces.
// `id` is the mojo workspace id — exactly what create-source's workspace_id wants.
interface WorkspaceRow {
  id: string;
  name: string;
  slug: string;
}

function DatasetsPage() {
  const api = useApi();
  const orgId = useActiveOrgId();
  // Demo sessions browse datasets read-only: no create, no delete.
  const isDemo = useIsDemoManage();
  const qc = useQueryClient();

  const datasets = useQuery({
    queryKey: ["admin-datasets", orgId],
    queryFn: () => api.request<DatasetSummary[]>(`/admin/orgs/${orgId}/datasets`),
  });

  // Which datasets are fed by a connector (icon/name/email shown per row). Fails
  // quietly (undefined) when the connectors feature is off.
  const sources = useQuery({
    queryKey: ["connector-sources"],
    queryFn: () => api.request<DatasetSourceInfo[]>(`/connectors/sources`),
    retry: false,
    enabled: !isDemo,
  });
  const sourceByDataset = new Map(
    (sources.data ?? []).map((s) => [s.dataset_id, s] as const),
  );

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.request<void>(`/admin/datasets/${id}`, { method: "DELETE" }, false),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-datasets", orgId] });
      toast.success("Dataset deleted");
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Delete failed");
    },
  });

  const total = datasets.data?.length ?? 0;
  const totalFiles =
    datasets.data?.reduce((acc, d) => acc + d.file_count, 0) ?? 0;
  const emptyDatasets =
    datasets.data?.filter((d) => d.file_count === 0).length ?? 0;

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Data"
        title="Datasets"
        description="Group files (PDF, CSV, XLSX, DOCX, PPTX) into reusable collections. CSV / XLSX files are ingested into DuckDB per-workspace at assignment time."
        actions={isDemo ? undefined : <CreateDatasetDialog orgId={orgId} />}
      />

      <StatStrip
        items={[
          { label: "Total", value: total, hint: "datasets" },
          { label: "Files", value: totalFiles, hint: "across all datasets" },
          { label: "Empty", value: emptyDatasets, hint: "no files yet" },
          {
            label: "Avg files",
            value: total ? Math.round((totalFiles / total) * 10) / 10 : 0,
            hint: "per dataset",
          },
        ]}
      />

      {/* data-tour anchor: the demo guided tour spotlights the datasets
          index table (wrapper div — Section doesn't spread props). */}
      <div data-tour="manage-datasets">
        <Section title="All datasets">
          {datasets.isLoading ? (
            <Skeleton className="h-40 w-full rounded-md" />
          ) : (
            <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-surface-soft)]">
                  <tr className="text-left">
                    <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Name
                    </th>
                    <th className="w-24 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Files
                    </th>
                    {!isDemo && (
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Synced from
                      </th>
                    )}
                    {!isDemo && (
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Sync
                      </th>
                    )}
                    <th className="w-32 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Updated
                    </th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                  {datasets.data?.length ? (
                    datasets.data.map((d) => (
                      <tr
                        key={d.id}
                        className="group transition hover:bg-[var(--color-surface)]"
                      >
                        <td className="px-4 py-3">
                          <Link
                            to="/manage/datasets/$id"
                            params={{ id: d.id }}
                            className="inline-flex items-center gap-2 font-medium text-foreground hover:text-[var(--color-primary)]"
                          >
                            <span>{d.icon ?? "📚"}</span>
                            {d.name}
                            <ArrowUpRight className="size-3 opacity-0 transition group-hover:opacity-60" />
                          </Link>
                          {d.description && (
                            <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                              {d.description}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                          {d.file_count}
                        </td>
                        {!isDemo && (
                          <td className="px-4 py-3">
                            {(() => {
                              const s = sourceByDataset.get(d.id);
                              return s ? (
                                <ConnectionChip
                                  provider={s.provider}
                                  name={s.display_name}
                                  account={s.account_label}
                                />
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              );
                            })()}
                          </td>
                        )}
                        {!isDemo && (
                          <td className="px-4 py-3">
                            {(() => {
                              const s = sourceByDataset.get(d.id);
                              return s ? (
                                <SyncDetail source={s} />
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              );
                            })()}
                          </td>
                        )}
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {new Date(d.updated_at).toLocaleDateString()}
                        </td>
                        <td className="px-2 py-2">
                          {!isDemo && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7 opacity-0 transition group-hover:opacity-100"
                              onClick={() => {
                                if (confirm(`Delete dataset "${d.name}"?`)) {
                                  remove.mutate(d.id);
                                }
                              }}
                              aria-label="Delete dataset"
                            >
                              <Trash2 className="size-3.5 text-muted-foreground" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <EmptyRow colSpan={isDemo ? 4 : 6}>
                      No datasets yet — create one and start uploading files.
                    </EmptyRow>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function CreateDatasetDialog({ orgId }: { orgId: string }) {
  const api = useApi();
  const qc = useQueryClient();
  // Demo tenants never see connector options — sync UI and its fetches are off.
  const isDemoDialog = useIsDemoManage();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Optional: sync the new dataset from an existing connection's folder.
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [connectionId, setConnectionId] = useState("");
  const [containerRef, setContainerRef] = useState<Json | null>(null);
  const [containerLabel, setContainerLabel] = useState<string | null>(null);
  const [frequency, setFrequency] = useState("manual");
  // "files" (drive folder) or "mail" (mailbox/label). A connection can offer
  // both (e.g. Google = Drive + Gmail); default to whichever it supports.
  const [sourceKind, setSourceKind] = useState<"files" | "mail">("files");
  // Which workspace the synced files land in. Required when syncing — the
  // backend otherwise falls back to the deployment-default workspace, so a
  // synced dataset would be invisible to the workspace the user is actually in.
  const [workspaceId, setWorkspaceId] = useState("");

  // Only offered if the org has at least one connection.
  const connections = useQuery({
    queryKey: ["connections", orgId],
    enabled: open && !isDemoDialog,
    queryFn: () => api.request<ConnectionOut[]>(`/connections`),
  });
  // Workspaces to pick as the sync destination (only fetched once syncing is on).
  const workspaces = useQuery({
    queryKey: ["admin-org-workspaces", orgId],
    enabled: open && syncEnabled,
    queryFn: () => api.request<WorkspaceRow[]>(`/admin/orgs/${orgId}/workspaces`),
  });
  const hasConnections = (connections.data?.length ?? 0) > 0;
  const sourceReady =
    syncEnabled && Boolean(connectionId) && Boolean(containerRef) && Boolean(workspaceId);

  function reset() {
    setName("");
    setDescription("");
    setSyncEnabled(false);
    setConnectionId("");
    setContainerRef(null);
    setContainerLabel(null);
    setFrequency("manual");
    setSourceKind("files");
    setWorkspaceId("");
  }

  const create = useMutation({
    mutationFn: async () => {
      const ds = await api.request<DatasetSummary>(`/admin/datasets`, {
        method: "POST",
        body: JSON.stringify({ org_id: orgId, name, description: description || null }),
      });
      // Then, if a source was configured, bind the folder and kick a first sync.
      if (sourceReady && containerRef) {
        await api.request<SourceOut>(`/connectors/sources`, {
          method: "POST",
          body: JSON.stringify({
            connection_id: connectionId,
            dataset_id: ds.id,
            kind: sourceKind,
            container_ref: containerRef,
            frequency,
            workspace_id: workspaceId,
          }),
        });
      }
      return ds;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-datasets", orgId] });
      await qc.refetchQueries({ queryKey: ["admin-datasets", orgId] });
      toast.success(sourceReady ? "Dataset created — first sync started" : "Dataset created");
      setOpen(false);
      reset();
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Create failed");
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger
        render={
          <Button>
            <Plus className="size-4" />
            New dataset
          </Button>
        }
      />
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New dataset</DialogTitle>
          <DialogDescription>A dataset is a named group of files.</DialogDescription>
        </DialogHeader>
        <div className="-mx-1 flex max-h-[65vh] flex-col gap-3 overflow-y-auto px-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ds-name">Name</Label>
            <Input
              id="ds-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q3 Reports"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ds-desc">Description (optional)</Label>
            <Textarea
              id="ds-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {hasConnections && (
            <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={syncEnabled}
                  onCheckedChange={(v) => setSyncEnabled(Boolean(v))}
                />
                Sync from a connection
              </label>

              {syncEnabled && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-medium text-muted-foreground">
                      Destination workspace
                    </Label>
                    <Select
                      value={workspaceId}
                      onValueChange={(v) => setWorkspaceId(v ?? "")}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Pick a workspace">
                          {(value: string | null) =>
                            workspaces.data?.find((w) => w.id === value)?.name ??
                            "Pick a workspace"
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="w-(--anchor-width)">
                        {workspaces.data?.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">
                      Synced files are indexed into this workspace.
                    </span>
                  </div>
                  <Select
                    value={connectionId}
                    onValueChange={(v) => {
                      const id = v ?? "";
                      setConnectionId(id);
                      setContainerRef(null);
                      setContainerLabel(null);
                      // Default the source kind to whatever the connection offers.
                      const picked = connections.data?.find((c) => c.id === id);
                      setSourceKind(
                        (picked?.ports ?? []).includes("file_source") ? "files" : "mail",
                      );
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Pick a connection">
                        {(value: string | null) => {
                          const c = connections.data?.find((x) => x.id === value);
                          return c ? (
                            <span className="flex min-w-0 items-center gap-2">
                              <ProviderIcon provider={c.provider} className="size-4 shrink-0" />
                              <span className="font-medium">{c.display_name}</span>
                              {c.account_label && (
                                <span className="truncate text-muted-foreground">
                                  · {c.account_label}
                                </span>
                              )}
                            </span>
                          ) : (
                            "Pick a connection"
                          );
                        }}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="w-(--anchor-width)">
                      {connections.data?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex min-w-0 items-center gap-2">
                            <ProviderIcon provider={c.provider} className="size-4 shrink-0" />
                            <span className="font-medium">{c.display_name}</span>
                            {c.account_label && (
                              <span className="truncate text-muted-foreground">
                                · {c.account_label}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {connectionId &&
                    (() => {
                      const picked = connections.data?.find((c) => c.id === connectionId);
                      const ports = picked?.ports ?? [];
                      const both =
                        ports.includes("file_source") && ports.includes("mailbox_source");
                      const isMail = sourceKind === "mail";
                      const pickKind = (k: "files" | "mail") => {
                        setSourceKind(k);
                        setContainerRef(null);
                        setContainerLabel(null);
                      };
                      return (
                        <>
                          {both && (
                            <div className="inline-flex self-start rounded-md border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] p-0.5 text-xs">
                              {(["files", "mail"] as const).map((k) => (
                                <button
                                  key={k}
                                  type="button"
                                  onClick={() => pickKind(k)}
                                  className={
                                    sourceKind === k
                                      ? "rounded bg-card px-2.5 py-1 font-medium text-foreground shadow-sm"
                                      : "rounded px-2.5 py-1 text-muted-foreground transition hover:text-foreground"
                                  }
                                >
                                  {k === "files" ? "Drive folder" : "Mailbox"}
                                </button>
                              ))}
                            </div>
                          )}
                          {isMail ? (
                            <MailboxPicker
                              connectionId={connectionId}
                              onSelect={(ref, label) => {
                                setContainerRef(ref);
                                setContainerLabel(label);
                              }}
                            />
                          ) : (
                            <FolderBrowser
                              connectionId={connectionId}
                              onSelect={(ref, label) => {
                                setContainerRef(ref);
                                setContainerLabel(label);
                              }}
                            />
                          )}
                          <div className="flex items-center justify-between gap-2">
                            <span className="min-w-0 truncate text-xs text-muted-foreground">
                              {containerRef ? (
                                <>
                                  {isMail ? "Mailbox: " : "Folder: "}
                                  <span className="text-foreground">
                                    {containerLabel || containerRefLabel(containerRef)}
                                  </span>
                                </>
                              ) : isMail ? (
                                "Choose a mailbox to sync"
                              ) : (
                                "Open a folder to sync"
                              )}
                            </span>
                            <FrequencySelect value={frequency} onChange={setFrequency} />
                          </div>
                        </>
                      );
                    })()}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            disabled={!name || create.isPending || (syncEnabled && !sourceReady)}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
